
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { SleepFormData } from "../../SleepFormContext";
import { SleepSessionUpdate } from "../sleep-dialog-types";

export function useSleepUpdateService() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateSleepSession = async (
    values: SleepFormData, 
    activityToEdit: any
  ) => {
    try {
      console.log(' Updating sleep session', { 
        id: activityToEdit.id,
        childId: values.childId, 
        originalChildId: activityToEdit.child_id || 'unknown' 
      });
      
      // Validate and ensure we have proper Date objects
      const startDate = values.start_time instanceof Date ? values.start_time : new Date(values.start_time);
      const endDate = values.end_time ? (values.end_time instanceof Date ? values.end_time : new Date(values.end_time)) : null;
      
      // Validate that dates are valid
      if (!startDate || isNaN(startDate.getTime())) {
        throw new Error('Invalid start time. Please select a valid date and time.');
      }
      
      if (endDate && isNaN(endDate.getTime())) {
        throw new Error('Invalid end time. Please select a valid date and time.');
      }
      
      const sleepRecord: SleepSessionUpdate & { has_woken_up?: string; status?: string } = {
        child_id: values.childId,
        caregiver_id: values.caregiverId,
        start_time: startDate.toISOString(),
        end_time: endDate ? endDate.toISOString() : null,
        location_id: values.location_id || null,
        environment_id: values.environment_id || null,
        onset_method_id: values.onset_method_id || null,
        restfulness_id: values.restfulness_id || null,
        signs_of_sleep_debt: values.signs_of_sleep_debt || false,
        sound_id: values.sound_id || null,
        sound_level_id: values.sound_level_id || null,
        notes: values.notes?.trim() || "",
        has_woken_up: values.has_woken_up || 'unknown',
        status: values.status || 'in_progress'
      };

      console.log(' Final database update object:', {
        ...sleepRecord,
        child_id_check: sleepRecord.child_id
      });
      
      const { data, error } = await supabase
        .from('sleep_sessions')
        .update(sleepRecord)
        .eq('id', activityToEdit.id)
        .select('*')
        .single();

      if (error) throw error;

      console.log(' Sleep session updated successfully:', {
        id: data.id,
        childId: data.child_id,
        start: data.start_time,
        end: data.end_time
      });

      queryClient.invalidateQueries({ queryKey: ['sleepSessions'] });
      queryClient.invalidateQueries({ queryKey: ['childSleepRecords', values.childId] });

      toast({
        title: "Success",
        description: values.status === 'in_progress'
          ? "Sleep session saved - you can finish it later"
          : "Sleep session updated successfully"
      });
      
      return true;
    } catch (error) {
      console.error(' Error in sleep session update:', error);
      
      toast({
        variant: "destructive",
        title: "Error Updating",
        description: error instanceof Error ? error.message : "Failed to update sleep session"
      });
      
      return false;
    }
  };

  return { updateSleepSession };
}
